<template>
    <div class="mt-4">
        <b-form-group
            :label="$t('form.label.forWhom')"
            label-for="select-users"
        >
            <select-with-avatar
                id="select-users"
                aria-describedby="select-users"
                :multiple="true"
                :loading="loading"
                :allowEmpty="true"
                :clear-on-select="true"
                :options="usersOptions"
                :close-on-select="false"
                :internal-search="false"
                :state="usersState"
                :selected.sync="$v.form.users.$model"
                :next-page-button="nextPageButton"
                :placeholder="$t('form.placeholder.sendFeedback')"
                :no-elements-found="$t('form.multiselect.noUserWasFound')"
                :noOptionsFound="$t('form.placeholder.emptyList')"
                @changeSearch="debouncedSearchHandler"
                @nextPage="nextPage"
            />
            <b-form-invalid-feedback id="select-users">
                <span v-if="userInvalid">
                    {{ $t('form.validations.users_required') }}
                </span>
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('form.label.template')"
            label-for="select-template-id"
        >
            <Select
                id="select-template-id"
                :state="askingsState"
                :selected="$v.form.template.$model"
                :options="templatesOptions"
                @changeValue="setTemplate"
                aria-describedby="template-feedback"
                :disabled="false"
                :placeholder="`${$t('talk.templates')}...`"
            />
            <b-form-invalid-feedback>
                <span v-if="asksInvalid">
                    {{ $t('form.validations.template_required') }}
                </span>
            </b-form-invalid-feedback>
        </b-form-group>
        <input-question
            :askings="askings"
            @changeAskings="updateAskings"
            :validation="$v.form.askings"
        />

        <div class="footer">
            <b-button
                variant="primary"
                size="sm"
                @click="onSubmit"
                :disabled="buttonDisabled"
            >
                <i v-if="isSubmitting" class="fa fa-spinner fa-spin px-2"></i>
                <span v-if="!isSubmitting">
                    {{ $t('form.button.send') }}
                </span>
            </b-button>
        </div>
    </div>
</template>

<script>
import { EventBuzz } from '@/main'
import Modal from '@/views/components/modal'
import Loader from '@/views/components/loader'
import SelectWithAvatar from '@/views/components/selects/select-with-avatar'
import { InputQuestion } from '@/views/profile/components/talk/components'
import {
    Select,
    mapperToOption,
    mapperToOptions,
} from '@/views/components/selects'
import { mapperToOptions as mapperToUsers } from '@/views/components/selects/select-with-avatar'

import { getUserData } from '@/services/user-data'
import { getUserPermissions } from '@/services/user-permissions'
import { getUser, getUsers, getTemplates, saveRequestFeedback } from '../api'

import notification, { success, danger } from '@/lib/notification'
import i18n from '@/i18n'

import { validationMixin } from 'vuelidate'
import { required, integer } from 'vuelidate/lib/validators'

import { sortBy, debounce } from 'lodash'

notification.init()

const INITIAL_FORM_STATE = {
    template: null,
    users: null,
    askings: null,
}

export default {
    name: 'SelfForm',
    components: {
        Modal,
        Loader,
        SelectWithAvatar,
        Select,
        InputQuestion,
    },
    mixins: [validationMixin],
    data() {
        return {
            userId: this.$store.state.currentUser.user.id,
            isSubmitting: false,
            currentUserId: null,
            currentUserHasTalk: false,
            usersOptions: [],
            defaultUsers: [],
            templatesOptions: [],
            templates: [],
            askings: [],
            usersReady: false,
            templatesReady: false,
            form: { ...INITIAL_FORM_STATE },
            loading: false,
            paginaton: {
                page: 1,
                lastPage: 1,
                perPage: 50,
            },
            search: null,
            isDefaultUsers: true,
            oldSearch: null,
        }
    },
    created() {
        this.debouncedSearchHandler = debounce(this.searchUserByName, 1000)
    },
    mounted() {
        const { id } = getUserData()
        this.currentUserId = id

        const { modules } = getUserPermissions()
        this.currentUserHasTalk = modules.has_talk

        if (this.currentUserHasTalk) {
            getUser(this.userId).then(resp => {
                const {
                    data: { data },
                } = resp
                const subordinates = data.subordinates ? data.subordinates : []
                const manager = data.manager ? [data.manager] : []
                const user = [data]
                let defaultUsers = subordinates
                    .concat(manager)
                    .concat(user)
                    .filter(user => user.has_talk)
                defaultUsers = sortBy(defaultUsers, user => user.name)
                const users = mapperToUsers(defaultUsers)
                this.usersOptions = users.filter(user => {
                    if (
                        user.value != this.currentUserId &&
                        user.value == this.userId
                    ) {
                        this.setUsers([user])
                    }
                    return user.value !== this.currentUserId
                })
                this.defaultUsers = this.usersOptions
                this.usersReady = true
            })

            getTemplates().then(resp => {
                this.templates = resp.data.data
                const defaultTemplate = {
                    id: 'default',
                    title: i18n.t('talk.noTemplate'),
                }

                this.templatesOptions = mapperToOptions([
                    defaultTemplate,
                    ...this.templates,
                ])
                this.setTemplate(mapperToOption(defaultTemplate))
                this.templatesReady = true
            })
        }
    },
    methods: {
        searchUserByName(search) {
            this.search = search
            if (search) {
                this.loading = true

                if (this.isDefaultUsers || this.oldSearch != this.search) {
                    this.usersOptions = []
                    this.resetPagination()
                }

                const params = {
                    orderBy: 'name',
                    filter: 'id;name;occupation;picture',
                    search: `id:${this.currentUserId};name:${search.trim() ||
                        ''};has_talk:1`,
                    searchFields: 'id:!=;name:like;has_talk:=',
                    and: 'yes',
                    per_page: this.paginaton.perPage,
                    page: this.paginaton.page,
                }

                getUsers(params).then(resp => {
                    const {
                        data: { data },
                    } = resp
                    if (data.current_page > 1) {
                        this.usersOptions = this.usersOptions.concat(
                            mapperToUsers(data.data)
                        )
                    } else {
                        this.usersOptions = mapperToUsers(data.data)
                    }
                    this.paginaton.lastPage = data.last_page
                    this.loading = false
                    this.isDefaultUsers = false
                    this.oldSearch = search
                })
            } else {
                this.usersOptions = this.getDefaultUsers()
            }
        },
        getDefaultUsers() {
            this.isDefaultUsers = true
            this.resetPagination()
            return this.defaultUsers
        },
        resetPagination() {
            this.paginaton.page = 1
            this.paginaton.lastPage = 1
        },
        nextPage() {
            this.paginaton.page++
            this.searchUserByName(this.search)
        },
        checkState(val) {
            const field = this.$v.form[val] || val
            return field.$dirty ? !field.$invalid : null
        },
        validate() {
            this.$v.$touch()
            this.setAskinsgDirty()
            return this.isValid && this.form.askings.length
        },
        setAskinsgDirty() {
            this.form.askings.forEach(ask => {
                ask.dirty = true
            })
        },
        onSubmit(event) {
            event.preventDefault()
            if (this.validate()) {
                this.isSubmitting = true

                const askings = this.form.askings.filter(asking =>
                    asking.title.trim()
                )

                const askingsTitles = askings.map(ask => ask.title)

                let feedbacks = []

                this.form.users.forEach(user => {
                    saveRequestFeedback(user.value, askingsTitles, [])
                        .then(response => {
                            feedbacks.push(response)
                            if (feedbacks.length == this.form.users.length) {
                                this.feedbackCreatedSuccessfully()
                                this.isSubmitting = false
                                this.$v.form.$reset()
                                this.form = { ...INITIAL_FORM_STATE }
                                this.askings = []
                                this.$emit('close-modal', true)
                            }
                        })
                        .catch(error => {
                            console.log(error)
                            this.notificationError()
                        })
                        .finally(() => {
                            EventBuzz.$emit('update-talk-summary')
                        })
                })
            }
        },
        setUsers(users) {
            this.$v.form.users = users
        },
        updateAskings(askings) {
            this.form.askings = askings
        },
        setTemplate(option) {
            const template = this.templates.find(
                template => template.id === option.value
            )

            this.askings = template && template.askings ? template.askings : []

            this.form.template = option
        },
        feedbackCreatedSuccessfully() {
            success(
                i18n.t('notification.createdSuccessfully', {
                    item: i18n.t('feedbacks.feedback'),
                })
            )
        },
        notificationError() {
            danger()
        },
        formState() {
            return this.$v.form.$anyDirty ? !this.$v.form.$invalid : null
        },
    },
    computed: {
        isModalLoading() {
            return !(this.usersReady && this.templatesReady)
        },
        disabledOkButton() {
            return this.isModalLoading || this.isSubmitting
        },
        isValid() {
            return !this.$v.form.$anyError
        },
        nextPageButton() {
            return {
                isShowNextPage:
                    this.paginaton.lastPage > 1 && !this.isDefaultUsers,
                isLastPage: this.paginaton.page == this.paginaton.lastPage,
                loading: this.loading,
            }
        },
        usersState() {
            return this.checkState('users')
        },
        userInvalid() {
            return this.$v.form.users.$dirty && !this.$v.form.users.required
        },
        askingsState() {
            return this.checkState('askings')
        },
        asksInvalid() {
            return this.$v.form.askings.$dirty && !this.$v.form.askings.required
        },
        buttonDisabled() {
            return !this.formState()
        },
    },
    validations: {
        form: {
            template: {},
            users: {
                required,
                $each: {
                    value: {
                        integer,
                        required,
                    },
                    label: {
                        required,
                    },
                },
            },
            askings: {
                required,
                $each: {
                    title: {
                        required,
                    },
                },
            },
        },
    },
}
</script>

<style lang="sass" scoped>
.footer
    width: 100%
    display: flex
    justify-content: flex-end
    align-items: center
    margin-top: 20px
    button
        width: 78px
</style>
